import { useState } from 'react';
import { useMoveConfirmation } from './MoveConfirmationFields';
import { DateTime } from 'luxon';
import { ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Label } from '@/components/atoms/label';
import { createUuid } from '@notifi/uuid/browser';
import { useArrivalWindowPresets } from '@/hooks/scheduling-v2/useArrivalWindowPresets';
import { applicableArrivalPresets } from './ArrivalWindowPresetPicker.logic';
import { formatPresetOption } from '@/components/organisms/settings-booking-windows/arrival-window-format';
import type {
  CalendarArrivalWindowChange,
  CalendarArrivalWindowConflict,
} from '@/lib/scheduler/calendarArrivalWindowChange';

export function CalendarArrivalWindowDialog({
  conflict,
  onCancel,
  onConfirm,
}: {
  conflict: CalendarArrivalWindowConflict;
  onCancel: () => void;
  onConfirm: (change: CalendarArrivalWindowChange) => void;
}) {
  const confirmation = useMoveConfirmation(conflict.appointmentId);
  const [selectedId, setSelectedId] = useState('');
  const start = DateTime.fromISO(conflict.proposedStartAt).setZone(conflict.timezone);
  const date = start.toISODate() ?? undefined;
  const context = {
    jobTypeId: conflict.jobTypeId ?? undefined,
    businessUnitId: conflict.businessUnitId ?? undefined,
    date,
  };
  const query = useArrivalWindowPresets({
    effective: true,
    jobTypeId: context.jobTypeId,
    businessUnitId: context.businessUnitId,
  });
  const presets = applicableArrivalPresets(query.data ?? [], context);
  const includesStart = (preset: (typeof presets)[number]) => {
    const from = DateTime.fromISO(`${date}T${preset.startTime}`, { zone: conflict.timezone });
    const until = DateTime.fromISO(`${date}T${preset.endTime}`, { zone: conflict.timezone });
    return start >= from && start <= until;
  };
  const selected = selectedId
    ? presets.find((preset) => preset.id === selectedId && includesStart(preset))
    : presets.find(includesStart);
  const customSelected = selectedId === 'visit-time' || (!selectedId && !selected);
  const canAccept = !query.isPending && !query.isError && (Boolean(selected) || customSelected);
  const format = (value: string | null) =>
    value
      ? DateTime.fromISO(value).setZone(conflict.timezone).toFormat('MMM d, yyyy, h:mm a ZZZZ')
      : 'Not set';
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Accept a new arrival window?</DialogTitle>
          <DialogDescription>
            This move is outside the current arrival window. Accept the new window below to save it
            together with the move, or cancel to keep the current schedule.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1 rounded-md bg-muted p-3 text-xs">
          <p>
            Current arrival:{' '}
            {conflict.currentDate ??
              `${format(conflict.currentStartAt)} – ${format(conflict.currentEndAt)}`}
          </p>
          <p className="font-medium">New start: {format(conflict.proposedStartAt)}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="calendar-arrival-window">Arrival window</Label>
          <div className="relative">
            <select
              id="calendar-arrival-window"
              value={customSelected && !query.isPending ? 'visit-time' : (selected?.id ?? '')}
              onChange={(event) => setSelectedId(event.target.value)}
              disabled={query.isPending || query.isError}
              className="h-9 w-full appearance-none rounded-md border border-input bg-card px-3 pr-9 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">
                {query.isPending ? 'Loading windows…' : 'Select an arrival window'}
              </option>
              <option value="visit-time">
                Custom · {start.toFormat('h:mm a')}–
                {DateTime.fromISO(conflict.proposedEndAt)
                  .setZone(conflict.timezone)
                  .toFormat('h:mm a')}
              </option>
              {presets.map((preset) => (
                <option key={preset.id} value={preset.id} disabled={!includesStart(preset)}>
                  {formatPresetOption(preset)}
                  {!includesStart(preset) ? ' (outside new start time)' : ''}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden
              className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Windows for {start.toFormat('MMM d, yyyy')} · {conflict.timezone}
          </p>
          {query.isError ? (
            <p className="text-xs text-destructive">
              Could not load arrival windows.{' '}
              <button className="underline" onClick={() => void query.refetch()}>
                Retry
              </button>
            </p>
          ) : !query.isPending && !presets.some(includesStart) ? (
            <p className="text-xs text-muted-foreground">
              No preset includes this time. Accept to create a custom arrival window matching the
              new visit time.
            </p>
          ) : null}
        </div>
        {confirmation.fields}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel move
          </Button>
          <Button
            disabled={!canAccept || !confirmation.canConfirm}
            onClick={() =>
              canAccept &&
              onConfirm({
                ...(customSelected ? { useVisitTime: true } : { presetId: selected!.id }),
                expectedUpdatedAt: conflict.expectedUpdatedAt,
                ...(confirmation.choice
                  ? {
                      notification: {
                        ...confirmation.choice,
                        includeInitialConfirmation: true,
                        requestId: createUuid(),
                      },
                    }
                  : {}),
              })
            }
          >
            Accept and move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
